import { useContext, useEffect } from "react";
import { CadviewerContext } from "../../../context/Cadviewer.Context";
import useConfig from "../../../demos/hooks/useConfig";
import { AIChatV5, useAIChatStoreV5 } from "../stores/aiChatStoreV5";
import { ChatMessageV5, ChatSuccessResponseV5 } from "../types/aiChatClientV5";

export interface UseAIChatV5ReturnType {
  messages: ChatMessageV5[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  thinkingText: string | null;
  thinkingStatuses: { status: string; message: string }[];
  sendMessage: (prompt: string, useGlobalMode?: boolean, selectedSpaces?: string[], searchMode?: "global" | "current" | "property", isChartMode?: boolean, followUpContext?: string) => Promise<void>;
  cancelQuery: () => void;
  currentSearchResult: ChatSuccessResponseV5 | null;
  clearError: () => void;
  clearHistoryMessagesToSend: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessageV5) => void;
  currentHighlight: string | null;
  clearCurrentHighlight: () => void;
  addLocalMessage: (content: string, result?: any) => void;
}

export function useAIChatV5(): UseAIChatV5ReturnType {
  const { plansInfo, distanceData, selectedPlan, selectedPlanDetail } =
    useContext(CadviewerContext);
  const { config } = useConfig();
  const spacesNames =
    plansInfo?.spaces.map((space) => ({
      name: space.spaceHeader,
      dbas: [],
    })) || [];

  const messages = useAIChatStoreV5((state: AIChatV5) => state.messages);
  const isLoading = useAIChatStoreV5((state: AIChatV5) => state.isLoading);
  const isSearching = useAIChatStoreV5((state: AIChatV5) => state.isSearching);
  const error = useAIChatStoreV5((state: AIChatV5) => state.error);
  const thinkingText = useAIChatStoreV5(
    (state: AIChatV5) => state.thinkingText
  );
  const thinkingStatuses = useAIChatStoreV5(
    (state: AIChatV5) => state.thinkingStatuses
  );
  const currentSearchResult = useAIChatStoreV5(
    (state: AIChatV5) => state.currentSearchResult
  );
  const currentHighlight = useAIChatStoreV5(
    (state: AIChatV5) => state.currentHighlight
  );
  const clearCurrentHighlight = useAIChatStoreV5(
    (state: AIChatV5) => state.clearCurrentHighlight
  );
  const sendMessage = useAIChatStoreV5((state: AIChatV5) => state.sendMessage);
  const cancelQuery = useAIChatStoreV5((state: AIChatV5) => state.cancelQuery);
  const mockSendMessage = async (
    prompt: string,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[],
    searchMode: "global" | "current" | "property" = "global",
    isChartMode: boolean = false,
    followUpContext?: string
  ) => {
    // Determine what to pass based on searchMode
    const planToPass = searchMode !== "global" ? selectedPlan : undefined;
    const planDetailToPass = searchMode === "current" ? selectedPlanDetail : undefined;

    await sendMessage(
      prompt,
      spacesNames,
      planToPass,
      planDetailToPass,
      useGlobalMode,
      selectedSpaces,
      isChartMode,
      followUpContext
    );
  };
  const clearError = useAIChatStoreV5((state: AIChatV5) => state.clearError);
  const clearHistoryMessagesToSend = useAIChatStoreV5(
    (state: AIChatV5) => state.clearHistoryMessagesToSend
  );
  const internalCADViewerAICallback = useAIChatStoreV5(
    (state: AIChatV5) => state.internalCADViewerAICallback
  );
  const addLocalMessage = useAIChatStoreV5(
    (state: AIChatV5) => state.addLocalMessage
  );
  const setPlansInfoAndDistanceData = useAIChatStoreV5(
    (state: AIChatV5) => state.setPlansInfoAndDistanceData
  );
  const setConfig = useAIChatStoreV5((state: AIChatV5) => state.setConfig);

  useEffect(() => {
    setPlansInfoAndDistanceData(plansInfo, distanceData);
  }, [plansInfo, distanceData, setPlansInfoAndDistanceData]);

  useEffect(() => {
    setConfig({
      aiChatV5Endpoint: config.aiChatV5Endpoint,
      aiChatV5Provider: config.aiChatV5Provider,
      aiChatV5Mode: config.aiChatV5Mode,
      analyticsTarget: config.analyticsTarget,
    });
  }, [
    config.aiChatV5Endpoint,
    config.aiChatV5Provider,
    config.aiChatV5Mode,
    config.analyticsTarget,
    setConfig,
  ]);

  return {
    messages,
    isLoading,
    isSearching,
    error,
    thinkingText,
    thinkingStatuses,
    sendMessage: mockSendMessage,
    cancelQuery,
    currentSearchResult,
    clearError,
    clearHistoryMessagesToSend,
    internalCADViewerAICallback,
    currentHighlight,
    clearCurrentHighlight,
    addLocalMessage,
  };
}
